import { apiFetch } from '../utils/apiFetch';
import type { ApiEndpoints } from '../types/ApiEndpoints';

export interface ArtistSocialLink {
  platform: string;
  url: string;
}

export interface ArtistMediaItem {
  id?: string;
  url: string;
  type?: 'image' | 'video' | 'youtube' | 'instagram';
  text?: string | null;
  copyright?: string | null;
  sortOrder?: number;
}

export interface ArtistProfile {
  id: string | null;
  name: string | null;
  slug: string | null;
  bio: string | null;
  avatar: string | null;
  socialLinks: ArtistSocialLink[];
  media: ArtistMediaItem[];
  /** Count of the user's public upcoming events (header stat). */
  eventsCount?: number;
  /** Year the user joined (from created_at). */
  joinedYear?: number | null;
}

export interface UpdateArtistProfilePayload {
  name?: string;
  bio?: string | null;
  slug?: string | null;
  social_links?: ArtistSocialLink[];
  media?: Array<{
    url: string;
    text?: string | null;
    copyright?: string | null;
    sort_order?: number;
  }>;
}

export class ArtistProfileValidationError extends Error {
  constructor(message: string, public errors: Record<string, string[]>) {
    super(message);
    this.name = 'ArtistProfileValidationError';
    Object.setPrototypeOf(this, ArtistProfileValidationError.prototype);
  }
}

/**
 * Client for the artist-profile API: the authenticated self-management
 * endpoints and the public read endpoints used by the public profile page.
 */
export class ArtistProfileService {
  constructor(
    private endpoints: Pick<ApiEndpoints, 'profile' | 'profileMedia' | 'profiles'>,
    private apiKey?: string,
    private partnerKey?: string | null
  ) {}

  /** Authenticated: fetch the current user's editable profile. */
  async getMyProfile(token?: string): Promise<ArtistProfile> {
    const res = await apiFetch(this.endpoints.profile, {
      method: 'GET',
      apiKey: this.apiKey,
      partnerKey: this.partnerKey,
      authToken: token,
    });
    if (!res.ok) {
      throw new Error('Failed to load profile');
    }
    return res.json();
  }

  /** Authenticated: persist the current user's profile (and the ordered media gallery). */
  async updateMyProfile(
    payload: UpdateArtistProfilePayload,
    token?: string
  ): Promise<ArtistProfile> {
    const res = await apiFetch(this.endpoints.profile, {
      method: 'PUT',
      apiKey: this.apiKey,
      partnerKey: this.partnerKey,
      authToken: token,
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (data?.errors && typeof data.errors === 'object') {
        throw new ArtistProfileValidationError(data.message || 'Validation failed', data.errors);
      }
      throw new Error(data?.message || 'Failed to update profile');
    }
    return data;
  }

  /** Authenticated: upload profile media files; returns the stored items to add to the gallery. */
  async uploadMedia(
    files: File[],
    copyrights: Array<string | undefined> = [],
    token?: string
  ): Promise<ArtistMediaItem[]> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files[]', file, file.name));
    copyrights.forEach((c) => formData.append('copyrights[]', c ?? ''));

    const res = await apiFetch(this.endpoints.profileMedia, {
      method: 'POST',
      apiKey: this.apiKey,
      partnerKey: this.partnerKey,
      authToken: token,
      body: formData,
    });
    if (!res.ok) {
      throw new Error('Failed to upload media');
    }
    const data = await res.json();
    return data.files ?? [];
  }

  /** Public: fetch a public profile by slug. Returns null when not found. */
  async getArtistBySlug(slug: string): Promise<ArtistProfile | null> {
    const res = await apiFetch(`${this.endpoints.profiles}/${encodeURIComponent(slug)}`, {
      method: 'GET',
      apiKey: this.apiKey,
      partnerKey: this.partnerKey,
    });
    if (res.status === 404) {
      return null;
    }
    if (!res.ok) {
      throw new Error('Failed to load artist');
    }
    return res.json();
  }
}
